import { navigateTo } from '@/utils/navigateTo';
import { useInkind } from '@gik/api/inkinds/inkind';
import type { SimilarProductsParams } from '@gik/api/products/similarProducts';
import { CheckoutLauncher } from '@gik/checkout/components/CheckoutLauncher/CheckoutLauncher';
import {
  createCartItemFromProduct,
  productHasIntegratedCheckout,
  productIsGiftcard,
} from '@gik/checkout/utils/productUtils';
import { Breakpoint, useBreakpoint } from '@gik/core/hooks/CustomHooks';
import type { CartItem } from '@gik/core/models/gik/Order';
import type { Product } from '@gik/core/models/gik/Product';
import routes from '@gik/core/routes';
import type { UIComponent } from '@gik/core/types/UI';
import bemBlock, { useBemCN } from '@gik/core/utils/bemBlock';
import repeatElement from '@gik/core/utils/repeatElement';
import { getTailwindConfig } from '@gik/core/utils/tailwind';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import { Carousel } from '@gik/ui/Carousel';
import type { ProductTileDirection } from '@gik/ui/gik/ProductTile';
import { ProductTile, ProductTileToolbar } from '@gik/ui/gik/ProductTile';
import { FrameBone, ParagraphBone } from '@gik/ui/SkeletonLoader';
import classnames from 'classnames';
import React from 'react';

const twConfig = getTailwindConfig();

export type RelatedProductsProps = {
  inkindRouteId?: string;
  products?: Product[]; // initial
  skeletonItems?: number;
  searchParams: SimilarProductsParams;
} & UIComponent;

// const SlickButtonFix = ({ children, ...props }) => {
//   delete props.currentSlide;
//   delete props.slideCount;
//   return <span {...props}>{children}</span>;
// };

export function RelatedProductsComp({ products, inkindRouteId, className }: RelatedProductsProps): React.ReactElement {
  const bem = bemBlock('related-products');
  const blockClasses = classnames([bem(), 'gik-carousel-product-tiles', className || '']);

  let arrowsMinNum = 2;
  const isLgUp = useBreakpoint(Breakpoint.LG);
  const isXlUp = useBreakpoint(Breakpoint.XL);

  if (isLgUp) arrowsMinNum = 3;
  if (isXlUp) arrowsMinNum = 4;

  return (
    <Carousel
      className={blockClasses}
      dots={false}
      infinite={false}
      slidesToScroll={1}
      slidesToShow={4}
      arrowButtonSize="lg"
      arrows={products?.length > arrowsMinNum}
      responsive={[
        {
          breakpoint: parseInt(twConfig.theme.screens.xl),
          settings: {
            slidesToShow: 4,
          },
        },
        {
          breakpoint: parseInt(twConfig.theme.screens.lg),
          settings: {
            slidesToShow: 3,
          },
        },
        {
          breakpoint: parseInt(twConfig.theme.screens.md),
          settings: {
            slidesToShow: 2,
          },
        },
      ]}
      /*prevArrow={
        <SlickButtonFix>
          <Button
            className={bem('slick-button', [{ prev: true }])}
            circle
            variant="white"
            size={'base'}
            prepend={<SvgIcon Icon={ChevronLeftIcon} />}
          />
        </SlickButtonFix>
      }
      nextArrow={
        <SlickButtonFix>
          <Button
            className={bem('slick-button', [{ next: true }])}
            circle
            variant="white"
            size={'base'}
            prepend={<SvgIcon Icon={ChevronRightIcon} />}
          />
        </SlickButtonFix>
      }*/
    >
      {products?.map?.((product, index) => {
        return (
          <div key={index} className="gik-carousel-product-tile">
            {/* <RelatedProductTile
              item={product}
              inkindRouteId={inkindRouteId}
              toolbar={<ProductTileToolbar itemId={product.id} inkindRouteId={inkindRouteId} />}
            /> */}
            <ProductTile
              containImage={productIsGiftcard(product)}
              imageUrl={product.gridImage}
              onClick={() => navigateTo(`${routes.products}/${product.slug}`)}
              toolbar={<ProductTileToolbar itemId={product.id} inkindRouteId={inkindRouteId} />}
              direction={'column'}
              title={product.name}
            />
          </div>
        );
      })}
    </Carousel>
  );
}

export const RelatedProducts = withComponentErrorBoundary(RelatedProductsComp);

interface IRelatedProductTileProps {
  item: Product;
  inkindRouteId: string;
  toolbar: React.ReactNode;
  direction?: ProductTileDirection;
}

export function RelatedProductTileComp({
  item,
  inkindRouteId,
  toolbar,
  direction = 'column',
}: IRelatedProductTileProps) {
  const { data: inkind } = useInkind(inkindRouteId);
  const cartItem: CartItem = createCartItemFromProduct(item, inkind);
  const hasIntegratedCheckout: boolean = productHasIntegratedCheckout(item);
  const wishlistProductUrl = !hasIntegratedCheckout ? `/products/${item?.slug}` : null;

  return (
    <CheckoutLauncher
      key={item.id}
      initiatedOn="wishlist"
      cart={[cartItem]}
      cachedProducts={[item]}
      initialInkindRouteId={inkindRouteId}
      child={onClick => (
        <ProductTile
          containImage={productIsGiftcard(item)}
          imageUrl={item.gridImage}
          onClick={event => (wishlistProductUrl ? navigateTo(wishlistProductUrl) : onClick(event))}
          toolbar={toolbar}
          direction={direction}
          title={item.name}
        />
      )}
    />
  );
}

export const RelatedProductTile = withComponentErrorBoundary(RelatedProductTileComp);

export const RelatedProductsSkeleton = ({ skeletonItems = 4 }: Partial<RelatedProductsProps>): React.ReactElement => {
  const bem = useBemCN(`related-products-skeleton`);
  return (
    <div {...bem()}>
      {repeatElement(
        skeletonItems,
        <div {...bem('item')}>
          <FrameBone {...bem('image')} />
          <main>
            <ParagraphBone words={10} {...bem('text')} />
          </main>
        </div>
      )}
    </div>
  );
};
